import React, { useMemo, useState } from 'react';
import { Button, Label, FormGroup, Modal, ModalHeader, ModalBody, ModalFooter, Col, Row } from 'reactstrap';
import Select from 'react-select';

const normalizeText = (s) =>
  String(s ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const pickFirst = (...vals) => vals.find(v => String(v ?? '').trim().length > 0) || '';

const sortByPrimaryLabel = (a, b) => {
  const ap = (a?.primaryLabel || a?.label || '').toString();
  const bp = (b?.primaryLabel || b?.label || '').toString();
  return ap.localeCompare(bp, 'pt-BR', { sensitivity: 'base' });
};

const formatOptionsGeneric = (data = []) => {
  return (data || []).map(item => {
    const primary = pickFirst(item?.nome, item?.name, item?.titulo, item?.title, item?.label, `Espaço ${item?.id ?? ''}`);
    const secondary = pickFirst(item?.codigo, item?.numero, item?.sigla, item?.referencia);

    const search = [
      primary, secondary,
      item?.expositor_nome, item?.expositor?.nome_mapa, item?.expositor?.nome, item?.expositor?.nome_fantasia
    ].filter(Boolean).join(' | ');

    return {
      value: item?.id,
      label: primary,
      primaryLabel: primary,
      secondaryLabel: secondary,
      search,
    };
  }).sort(sortByPrimaryLabel);
};

const formatOptionsExpositor = (data = []) => {
  return (data || []).map(item => {
    const primary = pickFirst(item?.nome_mapa, item?.nome, item?.nome_fantasia);
    const secondary = pickFirst(
      (primary !== (item?.nome || '') ? item?.nome : ''),
      (primary !== (item?.nome_fantasia || '') ? item?.nome_fantasia : '')
    );

    const search = [
      item?.nome_mapa, item?.nome, item?.nome_fantasia,
      item?.razao_social, item?.cnpj
    ].filter(Boolean).join(' | ');

    return {
      value: item?.id,
      label: primary,
      primaryLabel: primary,
      secondaryLabel: secondary,
      search,
    };
  }).sort(sortByPrimaryLabel);
};

const filterOptionBySearch = (option, inputValue) => {
  const q = normalizeText(inputValue);
  if (!q) return true;
  const hay = normalizeText(option?.data?.search || option?.data?.label || '');
  return hay.includes(q);
};

const TwoLineOption = ({ primary, secondary, isValue }) => (
  <div style={{ lineHeight: 1.15 }}>
    <div style={{ fontWeight: 600, fontSize: isValue ? 13 : 13 }}>
      {primary}
    </div>
    {secondary ? (
      <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>
        {secondary}
      </div>
    ) : null}
  </div>
);

const formatOptionLabelTwoLines = (opt, { context }) => {
  const primary = opt?.primaryLabel || opt?.label || '';
  const secondary = opt?.secondaryLabel || '';
  return <TwoLineOption primary={primary} secondary={secondary} isValue={context === 'value'} />;
};

const ClearIndicatorLimpar = (props) => {
  const { innerProps } = props;
  return (
    <div
      {...innerProps}
      title="Limpar"
      aria-label="Limpar"
      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 8px', cursor: 'pointer', opacity: 0.85 }}
    >
      <span style={{ fontSize: 12, fontWeight: 600 }}>Limpar</span>
      <span style={{ fontSize: 14, lineHeight: 1 }}>✕</span>
    </div>
  );
};
const ModalFiltros = ({
  isOpen,
  toggle,
  estandes,
  tiposEspaco,
  setores,
  categorias,
  onAplicarFiltros,
  expositores
}) => {
  const initialState = {
    pesquisa: '',
    setor: null,
    categoria: null,
    tipoEspaco: null,
    expositores: null,
    espacos: null
  };

  const [filtros, setFiltros] = useState(initialState);
  const setorOptions = useMemo(() => formatOptionsGeneric(setores || []), [setores]);
  const categoriaOptions = useMemo(() => formatOptionsGeneric(categorias || []), [categorias]);

  const tipoEspacoOptions = useMemo(() => {
    const arr = (tiposEspaco || []).map(t => t.tipo_espaco).filter(Boolean);
    return formatOptionsGeneric(arr);
  }, [tiposEspaco]);

  const expositoresOptions = useMemo(() => formatOptionsExpositor(expositores || []), [expositores]);
  const estandesOptions = useMemo(() => formatOptionsGeneric(estandes || []), [estandes]);

  const showExpositores = expositoresOptions.length > 0;
  const showEstandes = estandesOptions.length > 0;
  const showSetor = setorOptions.length > 0;
  const showCategoria = categoriaOptions.length > 0;
  const showTipoEspaco = tipoEspacoOptions.length > 0;

  const noFiltersAvailable = !showExpositores && !showEstandes && !showSetor && !showCategoria && !showTipoEspaco;

  const handleSelectChange = (selected, name) => {
    setFiltros(prev => ({ ...prev, [name]: selected || null }));
  };

  // desktop trabalha com arrays nos filtrosAtivos; aqui mandamos 0 ou 1 item
  const oneOrEmpty = (opt) => (opt?.value ? [opt.value] : []);

  const handleLimpar = () => {
    setFiltros(initialState);

    onAplicarFiltros({
      pesquisa: '',
      setor: [],
      categoria: [],
      tipoEspaco: [],
      expositores: [],
      espacos: []
    });

    toggle();
  };

  const handleAplicar = () => {
    onAplicarFiltros({
      pesquisa: filtros.pesquisa || '',
      setor: oneOrEmpty(filtros.setor),
      categoria: oneOrEmpty(filtros.categoria),
      tipoEspaco: oneOrEmpty(filtros.tipoEspaco),
      expositores: oneOrEmpty(filtros.expositores),
      espacos: oneOrEmpty(filtros.espacos)
    });

    toggle();
  };

  const customStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
    menu: (base) => ({ ...base, zIndex: 99999 })
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>Filtrar Espaços</ModalHeader>

      <ModalBody>
        {noFiltersAvailable ? (
          <div style={{ padding: 12, borderRadius: 8, background: '#f8f9fa' }}>
            Nenhum filtro disponível para esta feira.
          </div>
        ) : (
          <>
            {showExpositores && (
              <Row>
                <Col md={12}>
                  <FormGroup>
                    <Label>Expositor</Label>
                    <Select
                      name="expositores"
                      value={filtros.expositores}
                      isClearable
                      onChange={(v) => handleSelectChange(v, 'expositores')}
                      options={expositoresOptions}
                      filterOption={filterOptionBySearch}
                      formatOptionLabel={formatOptionLabelTwoLines}
                      components={{ ClearIndicator: ClearIndicatorLimpar }}
                      placeholder="Selecione..."
                      styles={customStyles}
                      menuPortalTarget={document.body}
                    />
                  </FormGroup>
                </Col>
              </Row>
            )}

            {showEstandes && (
              <Row>
                <Col md={12}>
                  <FormGroup>
                    <Label>Espaço</Label>
                    <Select
                      name="espacos"
                      value={filtros.espacos}
                      isClearable
                      onChange={(v) => handleSelectChange(v, 'espacos')}
                      options={estandesOptions}
                      filterOption={filterOptionBySearch}
                      formatOptionLabel={formatOptionLabelTwoLines}
                      components={{ ClearIndicator: ClearIndicatorLimpar }}
                      placeholder="Selecione..."
                      styles={customStyles}
                      menuPortalTarget={document.body}
                    />
                  </FormGroup>
                </Col>
              </Row>
            )}

            {(showSetor || showCategoria) && (
              <Row>
                {showSetor && (
                  <Col md={6}>
                    <FormGroup>
                      <Label>Setor</Label>
                      <Select
                        name="setor"
                        value={filtros.setor}
                        isClearable
                        onChange={(v) => handleSelectChange(v, 'setor')}
                        options={setorOptions}
                        placeholder="Selecione..."
                        styles={customStyles}
                        menuPortalTarget={document.body}
                      />
                    </FormGroup>
                  </Col>
                )}

                {showCategoria && (
                  <Col md={6}>
                    <FormGroup>
                      <Label>Categoria</Label>
                      <Select
                        name="categoria"
                        value={filtros.categoria}
                        isClearable
                        onChange={(v) => handleSelectChange(v, 'categoria')}
                        options={categoriaOptions}
                        placeholder="Selecione..."
                        styles={customStyles}
                        menuPortalTarget={document.body}
                      />
                    </FormGroup>
                  </Col>
                )}
              </Row>
            )}

            {showTipoEspaco && (
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Tipo de Espaço</Label>
                    <Select
                      name="tipoEspaco"
                      value={filtros.tipoEspaco}
                      isClearable
                      onChange={(v) => handleSelectChange(v, 'tipoEspaco')}
                      options={tipoEspacoOptions}
                      placeholder="Selecione..."
                      styles={customStyles}
                      menuPortalTarget={document.body}
                    />
                  </FormGroup>
                </Col>
              </Row>
            )}
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={handleLimpar}>Limpar Filtros</Button>
        <Button color="primary" onClick={handleAplicar} disabled={noFiltersAvailable}>Aplicar Filtros</Button>
      </ModalFooter>
    </Modal>
  );
};

export default ModalFiltros;
